import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Shield, Briefcase, Clock, FileText, CreditCard, Edit2, Save, Loader2, CheckCircle, XCircle, AlertCircle, Eye, Pencil } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

// Custom styles for Quill editor
const quillStyles = `
  .ql-toolbar {
    border-top-left-radius: 0.5rem;
    border-top-right-radius: 0.5rem;
    border: none !important;
    background: #f8fafc;
  }
  .ql-container {
    border: none !important;
    font-family: inherit;
    font-size: 0.875rem;
  }
  .ql-editor {
    min-height: 120px;
    padding: 1rem;
  }
  .ql-editor.ql-blank::before {
    color: #94a3b8;
    font-style: normal;
  }
`;

const API = '/api';

interface InlineTextFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  editMode: boolean;
  placeholder?: string;
  type?: 'text' | 'email' | 'tel' | 'select';
}

// Inline editable field component
const InlineTextField: React.FC<InlineTextFieldProps> = ({ label, value, onChange, editMode, placeholder = '', type = 'text' }) => {
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  if (!editMode) {
    return (
      <div className="space-y-1">
        <p className="text-xs font-medium text-gray-500">{label}</p>
        <p className="text-sm text-gray-900">{value || '-'}</p>
      </div>
    );
  }

  return (
    <div className="group">
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      <div 
        className="relative cursor-text"
        onClick={() => !isEditing && setIsEditing(true)}
      >
        {type === 'select' ? (
          <select
            ref={inputRef as React.RefObject<HTMLSelectElement>}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            className="w-full border-2 border-gray-200 hover:border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:border-indigo-600 transition-colors"
          >
            {placeholder && <option value="">{placeholder}</option>}
            <option value="Norge">Norge</option>
            <option value="Sverige">Sverige</option>
            <option value="Danmark">Danmark</option>
          </select>
        ) : (
          <input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            type={type}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="w-full border-2 border-gray-200 hover:border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:border-indigo-600 transition-colors"
          />
        )}
        {!isEditing && (
          <div className="absolute right-3 top-1/2 -trangray-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
            <Pencil className="w-4 h-4 text-gray-400" />
          </div>
        )}
      </div>
    </div>
  );
};

interface InlineRichTextFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  editMode: boolean;
  placeholder?: string;
}

// Rich text editor for bio/description
const InlineRichTextField: React.FC<InlineRichTextFieldProps> = ({ label, value, onChange, editMode, placeholder = '' }) => {
  if (!editMode) {
    return value ? (
      <div className="space-y-2">
        <p className="text-xs font-medium text-gray-500">{label}</p>
        <div 
          className="text-sm text-gray-700 prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: value }}
        />
      </div>
    ) : null;
  }

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      <div className="border-2 border-gray-200 hover:border-gray-300 rounded-lg overflow-hidden focus-within:border-indigo-600 transition-colors">
        <ReactQuill
          value={value || ''}
          onChange={onChange}
          placeholder={placeholder}
          theme="snow"
          modules={{
            toolbar: [
              ['bold', 'italic', 'underline'],
              [{ 'list': 'ordered'}, { 'list': 'bullet' }],
              ['clean']
            ]
          }}
          className="bg-white"
        />
      </div>
    </div>
  );
};

const CASE_CATEGORIES = [
  'Arbeidsrett',
  'Familie & Arv',
  'Kontrakt',
  'Eiendom',
  'Forbruker',
  'Selskapsrett',
  'Skatt',
  'Erstatning'
];

const COMPETENCE_LEVELS = [
  { value: 'BEGINNER', label: 'Nybegynner', desc: 'Begrenset erfaring' },
  { value: 'INTERMEDIATE', label: 'God kunnskap', desc: '1-3 års erfaring' },
  { value: 'EXPERT', label: 'Ekspert', desc: '3+ års erfaring' }
];

interface FormData {
  name?: string;
  email?: string;
  phone?: string;
  advokatforeningsnummer?: string;
  firmaNavn?: string;
  land?: string;
  bio?: string;
  segment?: string;
  fagomraader?: string[];
  praksisomraader?: string[];
  kompetansenivaa?: Record<string, string>;
  minPris?: number;
  maxPris?: number;
  [key: string]: any;
}

const AFAProfileView: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [profile, setProfile] = useState<any>(null);
  const [formData, setFormData] = useState<FormData>({});

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const afaProfile = response.data.afa_profile || {};
      setProfile(afaProfile);
      setFormData(afaProfile);
      
      // If status is DRAFT or REJECTED, go to edit mode by default
      if (afaProfile.status === 'DRAFT' || afaProfile.status === 'REJECTED') {
        setEditMode(true);
      }
    } catch (error) {
      console.error('Fetch profile error:', error);
      toast.error('Kunne ikke hente profil');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateNestedField = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }));
  };

  const toggleFagomraade = (fagomraade) => {
    const current = formData.fagomraader || [];
    if (current.includes(fagomraade)) {
      updateField('fagomraader', current.filter(f => f !== fagomraade));
      const newKompetanse = { ...formData.kompetansenivaa };
      delete newKompetanse[fagomraade];
      updateField('kompetansenivaa', newKompetanse);
    } else {
      updateField('fagomraader', [...current, fagomraade]);
      updateField('kompetansenivaa', {
        ...(formData.kompetansenivaa || {}),
        [fagomraade]: 'INTERMEDIATE'
      });
    }
  };

  const setCompetenceLevel = (fagomraade, level) => {
    updateField('kompetansenivaa', {
      ...(formData.kompetansenivaa || {}),
      [fagomraade]: level
    });
  };

  const handleSave = async () => {
    // Validation
    if (!formData.bevillingsnummer) {
      toast.error('Vennligst fyll inn bevillingsnummer');
      return;
    }
    if (!formData.fagomraader || formData.fagomraader.length === 0) {
      toast.error('Velg minst ett fagområde');
      return;
    }
    if (!formData.payout_info?.account_number) {
      toast.error('Vennligst fyll inn kontonummer');
      return;
    }
    if (!formData.terms_accepted) {
      toast.error('Du må akseptere vilkårene');
      return;
    }

    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      
      // Save
      await axios.put(`${API}/afa/profile`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Submit for approval
      await axios.post(`${API}/afa/profile/submit`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('✅ Profil oppdatert og sendt inn!');
      setEditMode(false);
      fetchProfile();
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || 'Kunne ikke lagre profil');
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      DRAFT: { text: 'Utkast', color: 'bg-gray-100 text-gray-700', icon: AlertCircle },
      SUBMITTED: { text: 'Under vurdering', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
      APPROVED: { text: 'Godkjent', color: 'bg-green-100 text-green-700', icon: CheckCircle },
      REJECTED: { text: 'Avvist', color: 'bg-red-100 text-red-700', icon: XCircle }
    };
    
    const badge = badges[status] || badges.DRAFT;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${badge.color}`}>
        <Icon className="w-4 h-4" />
        {badge.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Custom CSS for Quill */}
      <style>{quillStyles}</style>
      
      {/* Sticky Header with Save Button */}
      {editMode && (
        <div className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-indigo-600" />
              <span className="text-sm font-medium text-gray-900">Du har ulagrede endringer</span>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={async () => {
                  setSaving(true);
                  try {
                    const token = localStorage.getItem('token');
                    await axios.put(`${API}/afa/profile`, formData, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    toast.success('✅ Utkast lagret');
                    fetchProfile();
                  } catch (error) {
                    console.error('Save error:', error);
                    toast.error('Kunne ikke lagre utkast');
                  } finally {
                    setSaving(false);
                  }
                }}
                disabled={saving}
                variant="outline"
                className="text-sm"
              >
                Lagre utkast
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm"
              >
                {saving ? 'Lagrer...' : 'Lagre og send inn'}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      <div className="max-w-4xl mx-auto py-8 px-4">
        {/* Header */}
        <div className="mb-6">
          <Button
            onClick={() => navigate('/lexy/dashboard')}
            variant="ghost"
            className="mb-4 text-gray-600 hover:text-gray-900"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Tilbake til dashboard
          </Button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                AFA-profil
              </h1>
              <p className="text-gray-600">
                Din Advokat for alle-profil og innstillinger
              </p>
            </div>
            <div>
              {getStatusBadge(profile?.status)}
            </div>
          </div>
        </div>

        {/* Rejection Notice */}
        {profile?.status === 'REJECTED' && profile?.rejection_reason && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex gap-3">
              <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-semibold text-red-900 mb-1">
                  Tilbakemelding fra admin
                </h3>
                <p className="text-sm text-red-800">
                  {profile.rejection_reason}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Edit/View Toggle */}
        {profile?.status !== 'SUBMITTED' && (
          <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {editMode ? (
                <>
                  <Pencil className="w-5 h-5 text-indigo-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Redigeringsmodus aktiv</p>
                    <p className="text-xs text-gray-600">Klikk på felt for å redigere direkte</p>
                  </div>
                </>
              ) : (
                <>
                  <Eye className="w-5 h-5 text-gray-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Forhåndsvisning</p>
                    <p className="text-xs text-gray-600">Slik vil profilen din se ut</p>
                  </div>
                </>
              )}
            </div>
            <Button
              onClick={() => setEditMode(!editMode)}
              variant="outline"
              className="border-gray-300 hover:border-gray-400"
            >
              {editMode ? (
                <>
                  <Eye className="w-4 h-4 mr-2" />
                  Forhåndsvisning
                </>
              ) : (
                <>
                  <Edit2 className="w-4 h-4 mr-2" />
                  Rediger
                </>
              )}
            </Button>
          </div>
        )}

        {/* Profile Content */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 divide-y divide-gray-200">
          {/* Section 1: Identitet */}
          <div className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Identitet & Bevilling</h2>
                <p className="text-xs text-gray-500">Din profesjonelle legitimasjon</p>
              </div>
            </div>

            <div className="space-y-4">
              <InlineTextField
                label="Bevillingsnummer *"
                value={formData.bevillingsnummer || ''}
                onChange={(val) => updateField('bevillingsnummer', val)}
                editMode={editMode}
                placeholder="Skriv inn ditt bevillingsnummer"
              />
              <InlineTextField
                label="Jurisdiksjon *"
                value={formData.jurisdiksjon || 'Norge'}
                onChange={(val) => updateField('jurisdiksjon', val)}
                editMode={editMode}
                type="select"
              />
            </div>
          </div>

          {/* Section 2: Kompetanse */}
          <div className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Fagområder & Kompetanse</h2>
                <p className="text-xs text-gray-500">Dine spesialiseringer og ekspertise</p>
              </div>
            </div>

            <div className="space-y-6">
              {/* Fagområder Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Fagområder * (Velg minst ett)
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {CASE_CATEGORIES.map(category => {
                    const isSelected = (formData.fagomraader || []).includes(category);
                    return (
                      <button
                        key={category}
                        onClick={() => editMode && toggleFagomraade(category)}
                        type="button"
                        disabled={!editMode}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                          isSelected
                            ? 'border-indigo-600 bg-gray-50'
                            : editMode 
                            ? 'border-gray-200 hover:border-gray-300 cursor-pointer'
                            : 'border-gray-200 opacity-60'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className={`text-sm font-medium ${isSelected ? 'text-indigo-900' : 'text-gray-700'}`}>
                            {category}
                          </span>
                          {isSelected && (
                            <CheckCircle className="w-5 h-5 text-indigo-600" />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Kompetansenivå */}
              {(formData.fagomraader || []).length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Kompetansenivå
                  </label>
                  <div className="space-y-3">
                    {(formData.fagomraader || []).map(fagomraade => (
                      <div key={fagomraade} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-sm font-semibold text-gray-900 mb-3">{fagomraade}</p>
                        <div className="grid grid-cols-3 gap-2">
                          {COMPETENCE_LEVELS.map(level => {
                            const isSelected = (formData.kompetansenivaa || {})[fagomraade] === level.value;
                            return (
                              <button
                                key={level.value}
                                type="button"
                                onClick={() => editMode && setCompetenceLevel(fagomraade, level.value)}
                                disabled={!editMode}
                                className={`p-3 rounded-lg border-2 text-xs text-center transition-all ${
                                  isSelected
                                    ? 'border-indigo-600 bg-gray-50 font-semibold text-indigo-900'
                                    : editMode
                                    ? 'border-gray-200 hover:border-gray-300 text-gray-700'
                                    : 'border-gray-200 text-gray-500 opacity-60'
                                }`}
                              >
                                <div className="font-medium">{level.label}</div>
                                <div className="text-xs opacity-75 mt-0.5">{level.desc}</div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Bio with Rich Text */}
              <InlineRichTextField
                label="Kort bio (valgfri)"
                value={formData.bio || ''}
                onChange={(val) => updateField('bio', val)}
                editMode={editMode}
                placeholder="Beskriv din bakgrunn, erfaring og ekspertise. Bruk formatering for å fremheve viktige punkter..."
              />
            </div>
          </div>

          {/* Section 3: Kapasitet */}
          <div className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Kapasitet & Responstid</h2>
                <p className="text-xs text-gray-500">Din arbeidskapasitet og forpliktelser</p>
              </div>
            </div>

            <div className="space-y-6">
              {/* Kapasitet per uke */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-3">
                  AFA-saker per uke *
                </label>
                <select
                  value={formData.afa_cases_per_week || 5}
                  onChange={(e) => editMode && updateField('afa_cases_per_week', parseInt(e.target.value))}
                  disabled={!editMode}
                  className={`w-full border-2 rounded-lg px-4 py-3 focus:outline-none focus:border-indigo-600 transition-colors ${
                    editMode 
                      ? 'border-gray-200 hover:border-gray-300 cursor-pointer bg-white' 
                      : 'border-gray-200 bg-gray-50 opacity-60'
                  }`}
                >
                  <option value={3}>3 saker/uke (Deltid)</option>
                  <option value={5}>5 saker/uke (Standard)</option>
                  <option value={10}>10 saker/uke (Fulltid)</option>
                  <option value={15}>15+ saker/uke (Høy kapasitet)</option>
                </select>
              </div>

              {/* SLA */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-3">
                  Ønsket SLA (Service Level Agreement) *
                </label>
                <div className="space-y-2">
                  {[
                    { value: 24, label: '24 timer', desc: 'Ekstra rask levering' },
                    { value: 48, label: '48 timer', desc: 'Standard levering' },
                    { value: 72, label: '72 timer', desc: 'Fleksibel levering' }
                  ].map(option => {
                    const isSelected = (formData.sla_hours || 48) === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => editMode && updateField('sla_hours', option.value)}
                        disabled={!editMode}
                        className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                          isSelected
                            ? 'border-indigo-600 bg-gray-50'
                            : editMode
                            ? 'border-gray-200 hover:border-gray-300'
                            : 'border-gray-200 opacity-60'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className={`font-semibold ${isSelected ? 'text-indigo-900' : 'text-gray-900'}`}>
                              {option.label}
                            </p>
                            <p className={`text-sm ${isSelected ? 'text-indigo-700' : 'text-gray-600'}`}>
                              {option.desc}
                            </p>
                          </div>
                          {isSelected && (
                            <CheckCircle className="w-5 h-5 text-indigo-600" />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Section 4: Terms */}
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-gray-700" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Vilkår</h2>
              </div>
            </div>

            {editMode ? (
              <div className="border border-gray-200 rounded-lg p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.terms_accepted || false}
                    onChange={(e) => updateField('terms_accepted', e.target.checked)}
                    className="mt-1"
                  />
                  <span className="text-sm text-gray-700">
                    Jeg forstår og aksepterer AFA-modellen, inkludert fastpris-struktur, scope-begrensninger og SLA-forpliktelser. *
                  </span>
                </label>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm">
                {profile?.terms_accepted ? (
                  <>
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-gray-900">Vilkår akseptert</span>
                    {profile?.terms_accepted_at && (
                      <span className="text-gray-500">• {new Date(profile.terms_accepted_at).toLocaleDateString('no-NO')}</span>
                    )}
                  </>
                ) : (
                  <>
                    <XCircle className="w-4 h-4 text-red-600" />
                    <span className="text-gray-900">Vilkår ikke akseptert</span>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Section 5: Payout */}
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-gray-700" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Utbetalingsinformasjon</h2>
              </div>
            </div>

            {editMode ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Kontonummer *
                  </label>
                  <input
                    type="text"
                    value={formData.payout_info?.account_number || ''}
                    onChange={(e) => updateNestedField('payout_info', 'account_number', e.target.value)}
                    placeholder="1234 56 78901"
                    className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-gray-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Organisasjonsnummer / Fødselsnummer *
                  </label>
                  <input
                    type="text"
                    value={formData.payout_info?.tax_id || ''}
                    onChange={(e) => updateNestedField('payout_info', 'tax_id', e.target.value)}
                    placeholder="123456789"
                    className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-gray-400"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Kontonummer:</span>
                  <span className="font-medium text-gray-900">
                    {profile?.payout_info?.account_number ? '••••' + profile.payout_info.account_number.slice(-4) : '-'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Org/Fødselsnr:</span>
                  <span className="font-medium text-gray-900">
                    {profile?.payout_info?.tax_id ? '••••••' + profile.payout_info.tax_id.slice(-3) : '-'}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Save Button (Edit Mode) */}
        {editMode && (
          <div className="bg-gray-50 border-2 border-indigo-200 rounded-xl p-4 mt-6">
            <div className="flex items-start gap-3 mb-4">
              <AlertCircle className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-indigo-900 mb-1">
                  Husk å lagre endringene dine!
                </p>
                <p className="text-xs text-indigo-700">
                  {profile?.status === 'APPROVED' 
                    ? 'Endringer må godkjennes på nytt av admin.' 
                    : 'Dine endringer vil sendes til godkjenning.'}
                </p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setEditMode(false);
                  setFormData(profile);
                }}
                variant="outline"
                className="flex-1"
              >
                Avbryt
              </Button>
              <Button
                onClick={async () => {
                  setSaving(true);
                  try {
                    const token = localStorage.getItem('token');
                    await axios.put(`${API}/afa/profile`, formData, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    toast.success('✅ Utkast lagret');
                    fetchProfile();
                  } catch (error) {
                    console.error('Save error:', error);
                    toast.error('Kunne ikke lagre utkast');
                  } finally {
                    setSaving(false);
                  }
                }}
                disabled={saving}
                variant="outline"
                className="flex-1 border-gray-300 hover:bg-gray-50"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Lagrer...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Lagre utkast
                  </>
                )}
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sender inn...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Lagre og send inn
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AFAProfileView;
